import { create } from "zustand";
import { conversationTemplates, getConversationTemplate } from "@samklang/content";
import {
  answerQuestion,
  createIdleConversation,
  goToPreviousQuestion,
  questionBudgetForPace,
  startConversation,
  type ConversationState,
  type SummaryItem,
} from "@samklang/conversation-engine";
import type { ConversationQuestion, ParticipantResponse, Perspective } from "@samklang/schemas";

export interface CommunicationPreferences {
  readonly communicationLevel: "enkel" | "standard";
  readonly pace: "standard" | "deep";
  readonly shortestWording: boolean;
  readonly visualSupport: boolean;
  readonly extraExamples: boolean;
  readonly allowDrawing: boolean;
  readonly textSize: "small" | "standard" | "large";
}

/** Maps legacy (pre-1.4) communication levels to the new Enkel/Standard set so saved conversations keep working. */
export function normalizeCommunicationLevel(value: unknown): CommunicationPreferences["communicationLevel"] {
  if (value === "enkel" || value === "younger" || value === "older" || value === "custom") return "enkel";
  return "standard";
}

/** Maps legacy Snabbkoll pace to the retired-pace standard budget so old saves keep working. */
export function normalizePace(value: unknown): CommunicationPreferences["pace"] {
  if (value === "deep") return "deep";
  return "standard";
}

export function normalizeCommunicationPreferences(value: Partial<CommunicationPreferences> | undefined): CommunicationPreferences {
  return {
    communicationLevel: normalizeCommunicationLevel(value?.communicationLevel),
    pace: normalizePace(value?.pace),
    shortestWording: value?.shortestWording ?? false,
    visualSupport: value?.visualSupport ?? true,
    extraExamples: value?.extraExamples ?? true,
    allowDrawing: value?.allowDrawing ?? true,
    textSize: value?.textSize ?? "standard",
  };
}

export interface FollowUpAction {
  readonly id: string;
  readonly description: string;
  readonly responsible: string;
  readonly startDate: string;
  readonly followUpDate: string;
  readonly hopedOutcome: string;
  readonly successIndicator: string;
  readonly status: "planned" | "in_progress" | "done" | "cancelled";
  readonly visibility: "shared" | "professional";
}

export interface ReviewedSummaryItem extends Omit<SummaryItem, "answer" | "source"> {
  readonly participantAnswer: string;
  readonly sharedAnswer: string;
  readonly participantPriority: number | null;
}

interface ConversationStore {
  readonly sessionId: string | undefined;
  readonly selectedTemplateId: string;
  readonly state: ConversationState;
  readonly preferences: CommunicationPreferences;
  readonly storageMode: "temporary" | "summary";
  readonly participantReference: string;
  readonly professionalCode: string;
  /** Perspective this session is collected from (defaults to "elev" for plain conversations). */
  readonly perspective: Perspective;
  /** Who conducted the conversation with the participant (free text, e.g. "Specialpedagog M. Lind"). */
  readonly conductedWith: string;
  readonly paused: boolean;
  readonly professionalNotes: Readonly<Record<string, string>>;
  readonly actions: readonly FollowUpAction[];
  readonly reviewedSummary: readonly ReviewedSummaryItem[];
  selectTemplate: (templateId: string) => void;
  setPreferences: (preferences: CommunicationPreferences) => void;
  setStorageMode: (mode: "temporary" | "summary") => void;
  setParticipantReference: (reference: string) => void;
  setProfessionalCode: (code: string) => void;
  setPerspective: (perspective: Perspective) => void;
  setConductedWith: (conductedWith: string) => void;
  start: () => string;
  answer: (response: ParticipantResponse) => void;
  previous: () => void;
  setPaused: (paused: boolean) => void;
  setProfessionalNote: (questionId: string, note: string) => void;
  addAction: (action: Omit<FollowUpAction, "id">) => void;
  removeAction: (actionId: string) => void;
  setReviewedSummary: (items: readonly ReviewedSummaryItem[]) => void;
  reset: () => void;
}

export const defaultPreferences: CommunicationPreferences = {
  communicationLevel: "standard",
  pace: "standard",
  shortestWording: false,
  visualSupport: true,
  extraExamples: true,
  allowDrawing: true,
  textSize: "standard",
};

function createSessionId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `session-${Date.now()}`;
}

export const useConversationStore = create<ConversationStore>((set, get) => ({
  sessionId: undefined,
  selectedTemplateId: conversationTemplates[0]?.id ?? "school-day",
  state: createIdleConversation(),
  preferences: defaultPreferences,
  storageMode: "temporary",
  participantReference: "",
  professionalCode: "",
  perspective: "elev",
  conductedWith: "",
  paused: false,
  professionalNotes: {},
  actions: [],
  reviewedSummary: [],
  selectTemplate: (selectedTemplateId) => {
    if (!getConversationTemplate(selectedTemplateId)) throw new Error("Samtalsmallen finns inte.");
    set({ selectedTemplateId });
  },
  setPreferences: (preferences) => set({ preferences }),
  setStorageMode: (storageMode) => set({ storageMode }),
  setParticipantReference: (participantReference) => set({ participantReference }),
  setProfessionalCode: (professionalCode) => set({ professionalCode }),
  setPerspective: (perspective) => set({ perspective }),
  setConductedWith: (conductedWith) => set({ conductedWith }),
  start: () => {
    const template = getConversationTemplate(get().selectedTemplateId);
    if (!template) throw new Error("Samtalsmallen finns inte.");
    const sessionId = createSessionId();
    const excludedQuestionKinds: ConversationQuestion["kind"][] = [];
    if (!get().preferences.allowDrawing) excludedQuestionKinds.push("drawing");
    set({
      sessionId,
      state: startConversation(template, { maxQuestions: questionBudgetForPace(get().preferences.pace), excludedQuestionKinds }),
      paused: false,
      professionalNotes: {},
      actions: [],
      reviewedSummary: [],
    });
    return sessionId;
  },
  answer: (response) => {
    const template = getConversationTemplate(get().selectedTemplateId);
    if (!template) throw new Error("Samtalsmallen finns inte.");
    set((current) => ({ state: answerQuestion(template, current.state, response) }));
  },
  previous: () => {
    const template = getConversationTemplate(get().selectedTemplateId);
    if (!template) return;
    set((current) => ({ state: goToPreviousQuestion(template, current.state) }));
  },
  setPaused: (paused) => set({ paused }),
  setProfessionalNote: (questionId, note) => set((current) => ({ professionalNotes: { ...current.professionalNotes, [questionId]: note } })),
  addAction: (action) => set((current) => ({ actions: [...current.actions, { ...action, id: createSessionId() }] })),
  removeAction: (actionId) => set((current) => ({ actions: current.actions.filter((action) => action.id !== actionId) })),
  setReviewedSummary: (reviewedSummary) => set({ reviewedSummary }),
  reset: () => set({ sessionId: undefined, state: createIdleConversation(), paused: false, professionalNotes: {}, actions: [], reviewedSummary: [], storageMode: "temporary", participantReference: "", professionalCode: "", perspective: "elev", conductedWith: "" }),
}));
